// Pure date-parsing utilities extracted from groundingService.ts so they can
// be tested in isolation. Keep this file dependency-free (no auth, no fetch,
// no logger imports beyond the optional one) — the tests should be cheap.

export type DateIntent = 'email' | 'calendar' | 'scheduling' | 'general';

export interface DateRange {
  startDateTime: string;
  endDateTime: string;
  dateRangeDescription: string;
}

const startOfDay = (d: Date) => { const n = new Date(d); n.setHours(0, 0, 0, 0); return n; };
const endOfDay = (d: Date) => { const n = new Date(d); n.setHours(23, 59, 59, 999); return n; };
const formatDate = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const formatDateFull = (d: Date) =>
  d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

const dayMap: { [key: string]: number } = {
  'sunday': 0, 'sun': 0,
  'monday': 1, 'mon': 1,
  'tuesday': 2, 'tue': 2, 'tues': 2,
  'wednesday': 3, 'wed': 3,
  'thursday': 4, 'thu': 4, 'thur': 4, 'thurs': 4,
  'friday': 5, 'fri': 5,
  'saturday': 6, 'sat': 6,
};

const monthMap: { [key: string]: number } = {
  'january': 0, 'jan': 0, 'february': 1, 'feb': 1, 'march': 2, 'mar': 2,
  'april': 3, 'apr': 3, 'may': 4, 'june': 5, 'jun': 5,
  'july': 6, 'jul': 6, 'august': 7, 'aug': 7, 'september': 8, 'sep': 8, 'sept': 8,
  'october': 9, 'oct': 9, 'november': 10, 'nov': 10, 'december': 11, 'dec': 11,
};

/**
 * Heuristic: does the user's query contain an explicit date/time reference?
 * Used by the email path to decide whether to narrow by date at all. If no
 * explicit signal is present, we search the whole mailbox — the prior
 * implementation silently capped to "past 7 days" which hid older emails.
 */
export function queryHasExplicitDate(query: string): boolean {
  const patterns = [
    /\b(today|yesterday|tomorrow)\b/i,
    /\b(this|last|next)\s+(week|month|year|quarter)\b/i,
    /\b(this|next)\s+(sunday|monday|tuesday|wednesday|thursday|friday|saturday)\b/i,
    /\b(past|previous|recent)\s+(\d+\s+)?(days?|weeks?|months?|years?|quarters?)\b/i,
    /\b\d{1,2}\/\d{1,2}(\/\d{2,4})?\b/,
    /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\b/i,
    /\bin\s+\d{4}\b/,
    /\b(q1|q2|q3|q4)\b/i,
    /\b(ytd|mtd|qtd)\b/i,
  ];
  return patterns.some((re) => re.test(query));
}

/**
 * Build a rich temporal context string injected into grounding prompts so
 * the model can resolve ambiguous phrases like "the week" deterministically.
 * `nowOverride` is for tests; production callers omit it.
 */
export function buildTemporalContext(nowOverride?: Date): string {
  const now = nowOverride ?? new Date();
  const today = now.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const time = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Chicago', timeZoneName: 'short' });
  const dayOfWeek = now.getDay();
  const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const monday = new Date(now);
  monday.setDate(now.getDate() + daysToMonday);
  monday.setHours(0, 0, 0, 0);
  const friday = new Date(monday);
  friday.setDate(monday.getDate() + 4);
  const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  const quarter = Math.floor(now.getMonth() / 3) + 1;
  return `CURRENT TIME: Today is ${today} at ${time}. Current business week runs ${fmt(monday)} through ${fmt(friday)}. Current quarter: Q${quarter} ${now.getFullYear()}.`;
}

/**
 * Dynamic date range parser. The `intent` argument chooses the fallback
 * window when no date phrase matches — calendar prefers the current
 * business week, scheduling looks forward, email leans recent.
 *
 * `nowOverride` is for tests only; production callers omit it.
 */
export function parseDateRange(query: string, intent: DateIntent = 'general', nowOverride?: Date): DateRange {
  const now = nowOverride ?? new Date();
  const queryLower = query.toLowerCase();

  const getNextWeekday = (targetDay: number) => {
    const result = new Date(now);
    const currentDay = result.getDay();
    let daysToAdd = targetDay - currentDay;
    if (daysToAdd <= 0) daysToAdd += 7;
    result.setDate(result.getDate() + daysToAdd);
    return result;
  };

  const getThisWeekday = (targetDay: number) => {
    const result = new Date(now);
    const currentDay = result.getDay();
    const daysToAdd = targetDay - currentDay;
    result.setDate(result.getDate() + daysToAdd);
    return result;
  };

  let start: Date;
  let end: Date;
  let description: string;

  if (/\byesterday\b/i.test(queryLower)) {
    start = startOfDay(new Date(now.getTime() - 24 * 60 * 60 * 1000));
    end = endOfDay(start);
    description = `yesterday (${formatDateFull(start)})`;
  } else if (/\btoday\b/i.test(queryLower)) {
    start = startOfDay(now);
    end = endOfDay(now);
    description = `today (${formatDateFull(start)})`;
  } else if (/\btomorrow\b/i.test(queryLower)) {
    start = startOfDay(new Date(now.getTime() + 24 * 60 * 60 * 1000));
    end = endOfDay(start);
    description = `tomorrow (${formatDateFull(start)})`;
  } else if (/\bnext week\b/i.test(queryLower)) {
    const dayOfWeek = now.getDay();
    const daysUntilNextMonday = dayOfWeek === 0 ? 1 : (8 - dayOfWeek);
    start = startOfDay(new Date(now));
    start.setDate(now.getDate() + daysUntilNextMonday);
    end = new Date(start);
    end.setDate(start.getDate() + 4);
    end = endOfDay(end);
    description = `next week (${formatDate(start)} - ${formatDate(end)})`;
  } else if (/\bthis week\b/i.test(queryLower)) {
    start = startOfDay(now);
    const daysUntilFriday = Math.max(5 - now.getDay(), 0);
    end = new Date(now);
    end.setDate(now.getDate() + daysUntilFriday);
    end = endOfDay(end);
    description = `this week (${formatDate(start)} - ${formatDate(end)})`;
  } else if (/\bnext month\b/i.test(queryLower)) {
    start = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    end = new Date(now.getFullYear(), now.getMonth() + 2, 0, 23, 59, 59, 999);
    description = `next month (${start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })})`;
  } else if (/\blast\s+mon(?:t)?h\b/i.test(queryLower)) {
    start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
    description = `last month (${start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })})`;
  } else if (/\bthis month\b/i.test(queryLower)) {
    start = startOfDay(now);
    end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    description = `this month (through ${formatDate(end)})`;
  } else if (/\bnext (sunday|monday|tuesday|wednesday|thursday|friday|saturday|sun|mon|tue|tues|wed|thu|thur|thurs|fri|sat)\b/i.test(queryLower)) {
    const match = queryLower.match(/\bnext (sunday|monday|tuesday|wednesday|thursday|friday|saturday|sun|mon|tue|tues|wed|thu|thur|thurs|fri|sat)\b/i);
    if (match) {
      const targetDay = dayMap[match[1].toLowerCase()];
      start = startOfDay(getNextWeekday(targetDay));
      end = endOfDay(start);
      description = `next ${match[1]} (${formatDateFull(start)})`;
    } else {
      start = startOfDay(now);
      end = endOfDay(new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000));
      description = 'the next 7 days';
    }
  } else if (/(^|\s)(on )?(sunday|monday|tuesday|wednesday|thursday|friday|saturday)\b/i.test(queryLower)) {
    const match = queryLower.match(/\b(sunday|monday|tuesday|wednesday|thursday|friday|saturday)\b/i);
    if (match) {
      const targetDay = dayMap[match[1].toLowerCase()];
      const thisWeekDay = getThisWeekday(targetDay);
      start = thisWeekDay <= now ? startOfDay(getNextWeekday(targetDay)) : startOfDay(thisWeekDay);
      end = endOfDay(start);
      description = `${match[1]} (${formatDateFull(start)})`;
    } else {
      start = startOfDay(now);
      end = endOfDay(new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000));
      description = 'the next 7 days';
    }
  } else if (/\bin\s+(\d{4})\b/i.test(queryLower)) {
    const match = queryLower.match(/\bin\s+(\d{4})\b/i);
    if (match) {
      const year = parseInt(match[1]);
      start = new Date(year, 0, 1);
      end = new Date(year, 11, 31, 23, 59, 59, 999);
      description = `${year}`;
    } else {
      start = startOfDay(now);
      end = endOfDay(new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000));
      description = 'the next 7 days';
    }
  } else if (/\blast year\b/i.test(queryLower)) {
    const lastYear = now.getFullYear() - 1;
    start = new Date(lastYear, 0, 1);
    end = new Date(lastYear, 11, 31, 23, 59, 59, 999);
    description = `last year (${lastYear})`;
  } else if (/(january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|jun|jul|aug|sep|sept|oct|nov|dec)\s+\d{4}/i.test(queryLower)) {
    const match = queryLower.match(/\b(january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|jun|jul|aug|sep|sept|oct|nov|dec)\s+(\d{4})\b/i);
    if (match) {
      const targetMonth = monthMap[match[1].toLowerCase()];
      const year = parseInt(match[2]);
      start = new Date(year, targetMonth, 1);
      end = new Date(year, targetMonth + 1, 0, 23, 59, 59, 999);
      description = `${start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`;
    } else {
      start = startOfDay(now);
      end = endOfDay(new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000));
      description = 'the next 7 days';
    }
  } else if (/(january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|jun|jul|aug|sep|sept|oct|nov|dec)\b/i.test(queryLower)) {
    const match = queryLower.match(/\b(january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|jun|jul|aug|sep|sept|oct|nov|dec)\b/i);
    if (match) {
      const targetMonth = monthMap[match[1].toLowerCase()];
      let year = now.getFullYear();
      if (targetMonth > now.getMonth()) year--;
      start = new Date(year, targetMonth, 1);
      end = new Date(year, targetMonth + 1, 0, 23, 59, 59, 999);
      description = `${start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`;
    } else {
      start = startOfDay(now);
      end = endOfDay(new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000));
      description = 'the next 7 days';
    }
  } else if (
    /\b(\d{1,2})\/(\d{1,2})(\/(\d{2,4}))?\b/.test(queryLower) ||
    /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]* (\d{1,2})(st|nd|rd|th)?\b/i.test(queryLower)
  ) {
    let targetDate: Date | null = null;

    const slashMatch = queryLower.match(/\b(\d{1,2})\/(\d{1,2})(\/(\d{2,4}))?\b/);
    if (slashMatch) {
      const month = parseInt(slashMatch[1]) - 1;
      const day = parseInt(slashMatch[2]);
      let year = slashMatch[4] ? parseInt(slashMatch[4]) : now.getFullYear();
      if (year < 100) year += 2000;
      targetDate = new Date(year, month, day);
    }

    if (!targetDate) {
      const monthDayMatch = queryLower.match(/\b(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]* (\d{1,2})(st|nd|rd|th)?\b/i);
      if (monthDayMatch) {
        const month = monthMap[monthDayMatch[1].toLowerCase()];
        const day = parseInt(monthDayMatch[2]);
        let year = now.getFullYear();
        if (new Date(year, month, day) < now) year++;
        targetDate = new Date(year, month, day);
      }
    }

    if (targetDate) {
      start = startOfDay(targetDate);
      end = endOfDay(targetDate);
      description = formatDateFull(start);
    } else {
      start = startOfDay(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000));
      end = endOfDay(now);
      description = 'the past 7 days';
    }
  } else {
    if (intent === 'calendar') {
      const dayOfWeek = now.getDay();
      const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const monday = startOfDay(new Date(now));
      monday.setDate(now.getDate() + daysToMonday);
      const friday = new Date(monday);
      friday.setDate(monday.getDate() + 4);
      start = monday;
      end = endOfDay(friday);
      description = `this business week (${formatDate(start)} - ${formatDate(end)})`;
    } else if (intent === 'scheduling') {
      start = startOfDay(now);
      end = endOfDay(new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000));
      description = 'the next 14 days';
    } else {
      start = startOfDay(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000));
      end = endOfDay(now);
      description = 'the past 7 days';
    }
  }

  return {
    startDateTime: start.toISOString(),
    endDateTime: end.toISOString(),
    dateRangeDescription: description,
  };
}
